import { useState } from 'react'
import { toast } from 'sonner'
import dayjs from 'dayjs'
import { Building2, Plus, Loader2, Unplug, Trash2, Play } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog'
import { Loading, ErrorState, EmptyState } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import {
  useZaloAccounts,
  useConnectZaloOa,
  useDeleteZaloAccount,
  usePauseZaloAccount,
  useResumeZaloAccount,
  useZaloOaConfig,
  statusMeta,
  type ChannelAccount,
} from '@/hooks/use-integrations'
import { ChannelCard } from './channel-card'
import { ChannelSetupGuide } from './channel-setup-guide'
import { UngDungZaloRieng } from './ung-dung-zalo-rieng'
import type { OaConfigStatus } from '@/hooks/use-integrations'


/** Hiện khi backend chưa có khoá ứng dụng Zalo OA. */
function OaSetupGuide({ cfg }: { cfg: OaConfigStatus }) {
  return (
    <ChannelSetupGuide
      title="Chưa cấu hình ứng dụng Zalo OA"
      intro="Toàn bộ phần xử lý phía máy chủ đã sẵn sàng (uỷ quyền OAuth, làm mới token, webhook, cửa sổ chăm sóc khách hàng, ZNS). Chỉ còn thiếu khoá ứng dụng."
      steps={[
        <>
          Tạo ứng dụng tại{' '}
          <a href="https://developers.zalo.me/createapp" target="_blank" rel="noreferrer" className="font-medium text-primary underline">
            developers.zalo.me/createapp
          </a>{' '}
          rồi liên kết Official Account của công ty vào ứng dụng đó.
        </>,
        <>Trong mục <strong>Official Account API</strong>, khai hai đường dẫn bên dưới.</>,
        <>
          Lấy <strong>App ID</strong> và <strong>Secret Key</strong>, đặt vào{' '}
          <code className="rounded bg-background px-1">bizcrm_backend_source/.env</code> rồi khởi động lại backend.
        </>,
      ]}
      copyItems={[
        { label: 'Callback URL (mục Official Account API)', value: cfg.redirectUri },
        { label: 'Webhook URL (mục Webhook)', value: cfg.webhookUrl },
      ]}
      missing={cfg.missing.map((k) => `${k}=`)}
      footnote={
        <>
          Zalo chỉ chấp nhận đường dẫn <strong>công khai qua HTTPS</strong> — chạy máy cục bộ phải
          mở đường hầm (ngrok, Cloudflare Tunnel) rồi đặt{' '}
          <code className="rounded bg-background px-1">PUBLIC_API_URL</code> trỏ tới đó.
          {!cfg.webhookStrict && ' Webhook đang ở chế độ nới lỏng — bật OA_WEBHOOK_STRICT=true khi lên thật.'}
        </>
      }
    />
  )
}

export function ZaloOaTab() {
  // includeDisabled: OA đã tạm dừng PHẢI còn trong danh sách — trước đây bị
  // lọc mất, không có chỗ nào để xoá hẳn hay bật lại, trong khi webhook vẫn
  // đổ tin về và báo cáo vẫn đếm.
  const { data, isLoading, isError } = useZaloAccounts('oa', false, true)
  const { data: cfg } = useZaloOaConfig()
  const connect = useConnectZaloOa()
  const remove = useDeleteZaloAccount()
  const pause = usePauseZaloAccount()
  const resume = useResumeZaloAccount()
  const [toDelete, setToDelete] = useState<ChannelAccount | null>(null)

  const handleConnect = () => {
    connect.mutate(undefined, {
      onSuccess: (url) => {
        // Redirect sang trang uỷ quyền OAuth của Zalo
        window.location.href = url
      },
      onError: (e) => toast.error(apiError(e)),
    })
  }

  const handlePause = (id: string) => {
    pause.mutate(id, {
      onSuccess: () => toast.success('Đã tạm dừng OA — không nhận/gửi tin, không tính báo cáo mới'),
      onError: (e) => toast.error(apiError(e)),
    })
  }
  const handleResume = (id: string) => {
    resume.mutate(id, {
      onSuccess: () => toast.success('Đã bật lại OA'),
      onError: (e) => toast.error(apiError(e)),
    })
  }
  const handleDelete = () => {
    if (!toDelete) return
    remove.mutate(toDelete.id, {
      onSuccess: () => { toast.success('Đã xoá OA khỏi danh sách'); setToDelete(null) },
      onError: (e) => toast.error(apiError(e)),
    })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Kết nối Official Account (OA) để gửi/nhận tin nhắn qua Zalo OA API.
        </p>
        <Button onClick={handleConnect} disabled={connect.isPending || cfg?.configured === false}>
          {connect.isPending ? <Loader2 className="animate-spin" /> : <Plus />}
          Kết nối OA
        </Button>
      </div>

      {cfg && !cfg.configured && <OaSetupGuide cfg={cfg} />}

      <UngDungZaloRieng />

      {isLoading ? (
        <Loading label="Đang tải Zalo OA..." />
      ) : isError ? (
        <ErrorState />
      ) : !data?.length ? (
        <EmptyState
          icon={Building2}
          title="Chưa kết nối OA nào"
          description="Nhấn Kết nối OA và cấp quyền cho ứng dụng trên trang Zalo."
        />
      ) : (
        <div className="space-y-3">
          {data.map((acc) => (
            <ChannelCard
              key={acc.id}
              icon={<Building2 className="h-5 w-5" />}
              avatarUrl={acc.avatarUrl}
              title={acc.displayName || 'Zalo OA'}
              subtitle={
                <>
                  {acc.externalPageId ? `OA ID ${acc.externalPageId} · ` : ''}
                  {acc.lastConnectedAt
                    ? `Kết nối lần cuối ${dayjs(acc.lastConnectedAt).format('DD/MM/YYYY HH:mm')}`
                    : 'Chưa từng kết nối'}
                </>
              }
              status={statusMeta(acc.liveStatus, acc.isDisabled)}
              actions={
                <>
                  {acc.isDisabled ? (
                    <Button variant="default" size="sm" onClick={() => handleResume(acc.id)} disabled={resume.isPending} title="Bật lại OA">
                      <Play /> Bật lại
                    </Button>
                  ) : (
                    <Button variant="ghost" size="sm" onClick={() => handlePause(acc.id)} disabled={pause.isPending} title="Tạm dừng — OA vẫn trong danh sách">
                      <Unplug />
                    </Button>
                  )}
                  <Button
                    variant="ghost" size="sm" className="text-destructive hover:text-destructive"
                    onClick={() => setToDelete(acc)} title="Xoá khỏi danh sách"
                  >
                    <Trash2 />
                  </Button>
                </>
              }
            />
          ))}
        </div>
      )}

      <Dialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Xoá OA khỏi danh sách?</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p className="font-medium text-foreground">{toDelete?.displayName || 'Zalo OA'}</p>
                <p>OA <strong>biến mất khỏi màn Tích hợp</strong>, không nhận webhook, không gửi tin, không tính vào báo cáo mới. Muốn dùng lại phải uỷ quyền OAuth từ đầu.</p>
                <p>Hội thoại cũ vẫn xem được trong màn Chat bằng bộ lọc “Lưu trữ”.</p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setToDelete(null)}>Hủy</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={remove.isPending}>
              {remove.isPending && <Loader2 className="animate-spin" />} Xoá OA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
